import { useMemo } from 'react';
import { useUi } from './store';

/**
 * Bilingual copy (English / Arabic) with RTL.
 *
 * A flat dictionary rather than a library: the portal has roughly a hundred
 * strings, and shipping i18next to a page a customer loads on mobile data is a
 * poor trade. `t()` falls back to English when a key is missing in Arabic, so a
 * partial translation degrades rather than showing a raw key.
 */

export type Lang = 'en' | 'ar';

type Dict = Record<string, { en: string; ar: string }>;

export const strings: Dict = {
  // shell
  book_now: { en: 'Book now', ar: 'احجز الآن' },
  my_bookings: { en: 'My bookings', ar: 'حجوزاتي' },
  sign_in: { en: 'Sign in', ar: 'تسجيل الدخول' },
  sign_out: { en: 'Sign out', ar: 'تسجيل الخروج' },
  loading: { en: 'Loading…', ar: 'جارٍ التحميل…' },
  back: { en: 'Back', ar: 'رجوع' },
  continue: { en: 'Continue', ar: 'متابعة' },
  cancel: { en: 'Cancel', ar: 'إلغاء' },
  close: { en: 'Close', ar: 'إغلاق' },
  retry: { en: 'Try again', ar: 'أعد المحاولة' },

  // steps
  step_location: { en: 'Location', ar: 'الفرع' },
  step_services: { en: 'Services', ar: 'الخدمات' },
  step_staff: { en: 'Staff', ar: 'الموظف' },
  step_time: { en: 'Time', ar: 'الوقت' },
  step_confirm: { en: 'Confirm', ar: 'تأكيد' },

  // branch step
  choose_location: { en: 'Choose a location', ar: 'اختر الفرع' },
  choose_location_sub: { en: 'Where would you like to visit us?', ar: 'أين تود زيارتنا؟' },

  // services step
  choose_services: { en: 'Choose your services', ar: 'اختر خدماتك' },
  choose_services_sub: { en: 'Pick one or more. We will work out the total time for you.', ar: 'اختر خدمة أو أكثر. سنحسب الوقت الإجمالي لك.' },
  search_services: { en: 'Search services', ar: 'ابحث عن خدمة' },
  no_services: { en: 'No services are bookable online here yet.', ar: 'لا توجد خدمات متاحة للحجز إلكترونياً هنا بعد.' },
  no_search_results: { en: 'Nothing matched your search.', ar: 'لا توجد نتائج مطابقة.' },
  max_services_reached: { en: 'You have reached the maximum number of services for one booking.', ar: 'لقد وصلت إلى الحد الأقصى لعدد الخدمات في الحجز الواحد.' },
  selected: { en: 'selected', ar: 'محددة' },
  choose_category: { en: 'Choose a category', ar: 'اختر فئة' },
  choose_category_sub: { en: 'Pick a category to see the services inside it.', ar: 'اختر فئة لعرض الخدمات بداخلها.' },
  all_categories: { en: 'All categories', ar: 'كل الفئات' },
  service_count_one: { en: 'service', ar: 'خدمة' },
  service_count_many: { en: 'services', ar: 'خدمات' },
  category_empty: { en: 'This category has no bookable services.', ar: 'لا توجد خدمات متاحة للحجز في هذه الفئة.' },

  // staff step
  choose_staff: { en: 'Choose your specialist', ar: 'اختر المختص' },
  choose_staff_sub: { en: 'Or let us assign whoever is free.', ar: 'أو دعنا نختار من هو متاح.' },
  any_staff: { en: 'Any available', ar: 'أي موظف متاح' },
  any_staff_sub: { en: 'Best chance of your preferred time', ar: 'فرصة أفضل للحصول على الوقت المناسب' },
  no_staff: { en: 'Nobody at this location can perform all of the selected services.', ar: 'لا يوجد موظف في هذا الفرع يقدم كل الخدمات المختارة.' },

  // time step
  choose_time: { en: 'Choose a time', ar: 'اختر الوقت' },
  no_slots: { en: 'No times left on this day.', ar: 'لا توجد أوقات متاحة في هذا اليوم.' },
  day_closed: { en: 'Closed', ar: 'مغلق' },
  day_full: { en: 'Fully booked', ar: 'محجوز بالكامل' },
  day_holiday: { en: 'Holiday', ar: 'عطلة' },
  morning: { en: 'Morning', ar: 'صباحاً' },
  afternoon: { en: 'Afternoon', ar: 'بعد الظهر' },
  evening: { en: 'Evening', ar: 'مساءً' },

  // review
  review_title: { en: 'Review and confirm', ar: 'مراجعة وتأكيد' },
  notes_label: { en: 'Anything we should know?', ar: 'هل هناك ما تود إخبارنا به؟' },
  notes_placeholder: { en: 'Optional — allergies, preferences, parking…', ar: 'اختياري — حساسية، تفضيلات، مواقف…' },
  total: { en: 'Total', ar: 'الإجمالي' },
  duration: { en: 'Duration', ar: 'المدة' },
  deposit_due: { en: 'Deposit due now', ar: 'العربون المستحق الآن' },
  pay_rest_at_salon: { en: 'The rest is paid at the salon.', ar: 'يُدفع الباقي في الصالون.' },
  confirm_booking: { en: 'Confirm booking', ar: 'تأكيد الحجز' },
  pay_and_confirm: { en: 'Pay deposit and confirm', ar: 'ادفع العربون وأكّد' },
  choose_gateway: { en: 'Pay the deposit with', ar: 'ادفع العربون عبر' },
  gateway_needs_phone: { en: 'Sends a confirmation code to your mobile number.', ar: 'يرسل رمز تأكيد إلى رقم جوالك.' },
  holding_slot: { en: 'We are holding this time for you', ar: 'نحتفظ لك بهذا الوقت' },
  hold_expires_in: { en: 'Expires in', ar: 'ينتهي خلال' },
  hold_expired: { en: 'Your reservation expired. Please choose a time again.', ar: 'انتهت مدة الحجز المؤقت. يرجى اختيار الوقت مرة أخرى.' },

  // auth
  auth_title: { en: 'Sign in to book', ar: 'سجّل الدخول للحجز' },
  auth_sub_email: { en: 'We will e-mail you a 6-digit code.', ar: 'سنرسل لك رمزاً من 6 أرقام على البريد.' },
  auth_sub_sms: { en: 'We will text you a 6-digit code.', ar: 'سنرسل لك رمزاً من 6 أرقام برسالة نصية.' },
  auth_sub_whatsapp: { en: 'We will send you a 6-digit code on WhatsApp.', ar: 'سنرسل لك رمزاً من 6 أرقام على واتساب.' },
  email_label: { en: 'E-mail address', ar: 'البريد الإلكتروني' },
  phone_label: { en: 'Mobile number', ar: 'رقم الجوال' },
  name_label: { en: 'Your name', ar: 'اسمك' },
  send_code: { en: 'Send code', ar: 'أرسل الرمز' },
  code_label: { en: 'Enter the code', ar: 'أدخل الرمز' },
  code_sent_to: { en: 'Sent to', ar: 'أُرسل إلى' },
  verify: { en: 'Verify', ar: 'تحقق' },
  resend: { en: 'Resend code', ar: 'إعادة الإرسال' },
  resend_in: { en: 'Resend in', ar: 'إعادة الإرسال خلال' },
  change_details: { en: 'Use a different address', ar: 'استخدم عنواناً آخر' },
  change_number: { en: 'Use a different number', ar: 'استخدم رقماً آخر' },
  new_account_note: { en: 'Looks like you are new here — tell us your name.', ar: 'يبدو أنك جديد هنا — أخبرنا باسمك.' },
  country_label: { en: 'Country', ar: 'الدولة' },
  country_search: { en: 'Search country', ar: 'ابحث عن دولة' },
  country_none: { en: 'No country matches that', ar: 'لا توجد دولة مطابقة' },
  // Shown when WhatsApp would not take the message — most often because that
  // number has no WhatsApp account. The offer of an address is the way out.
  wa_undelivered: {
    en: 'We could not reach that number on WhatsApp.',
    ar: 'تعذّر إرسال الرمز إلى هذا الرقم على واتساب.',
  },
  use_email_instead: { en: 'Send the code to my e-mail instead', ar: 'أرسل الرمز إلى بريدي الإلكتروني بدلاً من ذلك' },
  send_to_email: { en: 'Send to e-mail', ar: 'أرسل إلى البريد' },

  // success
  booked_title: { en: 'You are booked', ar: 'تم الحجز' },
  booked_sub: { en: 'We have sent the details to you. See you soon!', ar: 'أرسلنا لك التفاصيل. نراك قريباً!' },
  reference: { en: 'Reference', ar: 'رقم الحجز' },
  add_to_calendar: { en: 'Add to calendar', ar: 'أضف إلى التقويم' },
  book_another: { en: 'Book another', ar: 'حجز آخر' },

  // my bookings
  upcoming: { en: 'Upcoming', ar: 'القادمة' },
  past: { en: 'Past', ar: 'السابقة' },
  no_bookings: { en: 'Nothing here yet.', ar: 'لا يوجد شيء هنا بعد.' },
  cancel_booking: { en: 'Cancel booking', ar: 'إلغاء الحجز' },
  reschedule: { en: 'Reschedule', ar: 'إعادة الجدولة' },
  cancel_confirm: { en: 'Cancel this booking?', ar: 'إلغاء هذا الحجز؟' },
  cancelled: { en: 'Cancelled', ar: 'ملغي' },
  deposit_paid: { en: 'Deposit paid', ar: 'تم دفع العربون' },

  // payment return
  checking_payment: { en: 'Checking your payment…', ar: 'جارٍ التحقق من الدفع…' },
  payment_failed: { en: 'The payment did not go through.', ar: 'لم تتم عملية الدفع.' },
  payment_failed_sub: { en: 'Nothing was charged. You can pick a time again.', ar: 'لم يتم خصم أي مبلغ. يمكنك اختيار وقت آخر.' },

  // errors
  err_generic: { en: 'Something went wrong. Please try again.', ar: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.' },
  err_portal_off: { en: 'Online booking is not available for this business.', ar: 'الحجز الإلكتروني غير متاح لهذا النشاط.' },
  err_slot_taken: { en: 'That time was just taken. Please pick another.', ar: 'تم حجز هذا الوقت للتو. يرجى اختيار وقت آخر.' },
  err_code_invalid: { en: 'That code is not correct.', ar: 'الرمز غير صحيح.' },
  err_code_expired: { en: 'That code has expired. Request a new one.', ar: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً.' },
  err_too_many: { en: 'Too many attempts. Please wait a moment.', ar: 'محاولات كثيرة. يرجى الانتظار قليلاً.' },
  err_blocked: { en: 'This account cannot book online. Please contact us.', ar: 'لا يمكن لهذا الحساب الحجز إلكترونياً. يرجى التواصل معنا.' },
  err_not_found: { en: 'We could not find this page.', ar: 'تعذر العثور على هذه الصفحة.' },
  err_no_tenant: { en: 'This booking link is not valid.', ar: 'رابط الحجز غير صالح.' },
  // The deposit is a gate: without it there is no appointment. "Something went
  // wrong" leaves the customer retrying a button that cannot work, so these two
  // say which of the pair it is — call us (nothing to retry) vs. try again.
  err_payment_unavailable: {
    en: 'Online payment is not available right now, and this booking needs a deposit. Please contact us to book.',
    ar: 'الدفع الإلكتروني غير متاح حالياً، وهذا الحجز يتطلب عربوناً. يرجى التواصل معنا لإتمام الحجز.',
  },
  err_payment_failed: {
    en: 'We could not start the payment. Please try again in a moment.',
    ar: 'تعذّر بدء عملية الدفع. يرجى المحاولة مرة أخرى بعد قليل.',
  },
};

/** Map an API error code to friendly copy; unknown codes fall back to generic. */
export const errorKeyFor = (code: string): keyof typeof strings => {
  const map: Record<string, keyof typeof strings> = {
    SLOT_UNAVAILABLE: 'err_slot_taken',
    HOLD_EXPIRED: 'hold_expired',
    HOLD_ALREADY_USED: 'err_generic',
    CODE_INVALID: 'err_code_invalid',
    CODE_ATTEMPTS_EXCEEDED: 'err_too_many',
    CODE_RATE_LIMITED: 'err_too_many',
    CODE_COOLDOWN: 'err_too_many',
    ACCOUNT_BLOCKED: 'err_blocked',
    PORTAL_DISABLED: 'err_portal_off',
    TENANT_NOT_FOUND: 'err_no_tenant',
    BOOKING_NOT_FOUND: 'err_not_found',
    DEPOSIT_UNAVAILABLE: 'err_payment_unavailable',
    DEPOSIT_FAILED: 'err_payment_failed',
  };
  return map[code] ?? 'err_generic';
};

export interface Translator {
  t: (key: keyof typeof strings) => string;
  lang: Lang;
  dir: 'rtl' | 'ltr';
  pick: <T extends { name: string; name_ar?: string | null }>(item: T) => string;
}

/**
 * Translation helpers for the current language.
 *
 * The returned object is memoised on `lang`, and that memoisation is load-
 * bearing rather than an optimisation. `t` is a natural dependency of
 * `useCallback`/`useEffect` in the data-loading components; if a fresh `t` were
 * handed out on every render, every such effect would re-fire on every render,
 * set state, and re-render — an infinite loop that freezes the tab before React
 * ever commits. Keep this memoised.
 */
export function useT(): Translator {
  const lang = useUi((s) => s.lang);
  return useMemo<Translator>(
    () => ({
      lang,
      dir: lang === 'ar' ? 'rtl' : 'ltr',
      t: (key) => strings[key]?.[lang] ?? strings[key]?.en ?? String(key),
      pick: (item) => (lang === 'ar' && item.name_ar ? item.name_ar : item.name),
    }),
    [lang],
  );
}
